import nacl from 'tweetnacl';
import { decodeUTF8, encodeUTF8, encodeBase64, decodeBase64 } from 'tweetnacl-util';

/**
 * Derive a deterministic encryption key from the clipboard ID
 * Same ID = Same key (no password needed)
 * Uses browser's SubtleCrypto API for key derivation
 */
const deriveKeyFromId = async (clipboardId) => {
    // Create a consistent 32-byte key from the clipboard ID using SHA-256
    const encoder = new TextEncoder();
    const data = encoder.encode(clipboardId);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return new Uint8Array(hashBuffer);
};

/**
 * Encrypt text without requiring user password
 */
export const encryptText = async (text, clipboardId) => {
    try {
        const key = await deriveKeyFromId(clipboardId);
        const nonce = nacl.randomBytes(24);
        const messageUint8 = decodeUTF8(text);
        
        const encrypted = nacl.secretbox(messageUint8, nonce, key);
        
        // Combine nonce + ciphertext and encode as base64
        const fullMessage = new Uint8Array(nonce.length + encrypted.length);
        fullMessage.set(nonce);
        fullMessage.set(encrypted, nonce.length);
        
        return encodeBase64(fullMessage);
    } catch (err) {
        console.error('Encryption failed:', err);
        throw err;
    }
};

/**
 * Decrypt text using the same clipboard ID
 */
export const decryptText = async (encryptedBase64, clipboardId) => {
    try {
        const key = await deriveKeyFromId(clipboardId);
        const fullMessage = decodeBase64(encryptedBase64);
        
        // Extract nonce and ciphertext
        const nonce = fullMessage.slice(0, 24);
        const ciphertext = fullMessage.slice(24);
        
        const decrypted = nacl.secretbox.open(ciphertext, nonce, key);
        
        if (!decrypted) {
            throw new Error('Decryption failed - wrong key or corrupted data');
        }
        
        return encodeUTF8(decrypted);
    } catch (err) {
        console.error('Decryption failed:', err);
        throw err;
    }
};
